/**
 * Browser fingerprint collection for audit context.
 * 
 * This module provides minimal fingerprint signal collection for
 * anti-fraud audit context. Raw signals are transient - only hashes
 * are persisted on the backend.
 * 
 * Security posture:
 * - Signals collected for audit context only
 * - No raw signals stored locally
 * - No user-facing anti-fraud mechanics disclosed
 * - Collection failure is non-blocking (fail-open)
 * 
 * Design:
 * - Reuses FingerprintSignals interface from api/auth.ts
 * - Minimal signals only - no additional browser probing
 * - Matches backend fingerprint.CaptureInput schema
 */

import type { FingerprintSignals } from '@/api/auth';

/**
 * Collect available browser fingerprint signals.
 * 
 * Returns undefined if collection fails - never throws.
 * Intro request submissions proceed without fingerprint in this case.
 * 
 * This function intentionally does NOT:
 * - Probe additional browser capabilities
 * - Use canvas/WebGL/Audio for fingerprinting
 * - Persist signals locally
 * - Reveal collection mechanics in UI
 */
export async function collectFingerprint(): Promise<FingerprintSignals | undefined> {
  // Check for browser environment
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return undefined;
  }

  try {
    const signals: FingerprintSignals = {
      user_agent: navigator.userAgent || '',
      accept_language: navigator.language || '',
      screen: window.screen ? `${window.screen.width}x${window.screen.height}` : '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      platform: navigator.platform || '',
      hardware_concurrency: navigator.hardwareConcurrency || 0,
      // Note: canvas_hash, webgl_vendor, webgl_renderer, audio_hash, fonts_hash
      // would require additional browser probing. These are left empty
      // to keep the helper minimal. The auth fingerprint collection
      // provides these for the session flow; intro requests use
      // the basic signals available without extra probing.
      canvas_hash: '',
      webgl_vendor: '',
      webgl_renderer: '',
      audio_hash: '',
      fonts_hash: '',
      touch_support: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      color_depth: window.screen?.colorDepth || 0,
      device_memory: (navigator as { deviceMemory?: number }).deviceMemory || 0,
    };

    return signals;
  } catch {
    // Collection failed - return undefined, don't block submission
    return undefined;
  }
}

/**
 * Type alias for clarity - intro request API uses this name.
 * Re-exports FingerprintSignals for use in intro request payload.
 */
export type FingerprintInput = FingerprintSignals;